import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  FileCheck,
  Lock,
  MessageSquare,
  Mic,
  Moon,
  ShieldCheck,
  Sparkles,
  Sun,
  UserCheck,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { Link } from "react-router-dom"

import { AmbientBackdrop } from "@/components/layout/hero"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { useTheme } from "@/hooks/use-theme"
import { cn } from "@/lib/utils"

/**
 * How PRAXA works, in one look - for anyone, no sign-in.
 *
 * Deliberately short: four things it does, each as one sentence and three
 * steps, and three promises. The first version explained every part in
 * paragraphs with technical detail folded underneath, and read like a manual;
 * the detail lives in `docs/` for anyone who wants it. Every line here is
 * still true of the code - short is not the same as rounded up.
 *
 * It calls no API and shows no data, which is why it can sit outside the
 * sign-in gate.
 */

interface Journey {
  icon: LucideIcon
  title: string
  line: string
  steps: [string, string, string]
  /** Tailwind classes for the card's colour - one theme colour per card. */
  tone: { badge: string; ring: string; glow: string; step: string }
}

const JOURNEYS: Journey[] = [
  {
    icon: BriefcaseBusiness,
    title: "Find jobs",
    line: "New postings for your roles, scored against your CV every morning.",
    steps: ["Fetch", "Match", "Score"],
    tone: {
      badge: "bg-primary/15 text-primary",
      ring: "hover:border-primary/50",
      glow: "from-primary/20",
      step: "border-primary/30 bg-primary/5",
    },
  },
  {
    icon: FileCheck,
    title: "Write applications",
    line: "Your CV in the job's own words - then a second AI checks every line.",
    steps: ["Tailor", "Check", "Approve"],
    tone: {
      badge: "bg-accent/15 text-accent",
      ring: "hover:border-accent/50",
      glow: "from-accent/20",
      step: "border-accent/30 bg-accent/5",
    },
  },
  {
    icon: Mic,
    title: "Practise interviews",
    line: "One question at a time - type or speak, and get a score on five points.",
    steps: ["Ask", "Answer", "Feedback"],
    tone: {
      badge: "bg-success/15 text-success",
      ring: "hover:border-success/50",
      glow: "from-success/20",
      step: "border-success/30 bg-success/5",
    },
  },
  {
    icon: MessageSquare,
    title: "Ask anything",
    line: "Answers from your CV and career guides - with the sources shown.",
    steps: ["Search", "Check", "Answer"],
    tone: {
      badge: "bg-warning/15 text-warning",
      ring: "hover:border-warning/50",
      glow: "from-warning/20",
      step: "border-warning/30 bg-warning/5",
    },
  },
]

const PROMISES: { icon: LucideIcon; title: string; line: string }[] = [
  { icon: ShieldCheck, title: "No invented facts", line: "A second AI flags anything your CV does not say." },
  { icon: Lock, title: "Only your data", line: "Your CVs and documents are yours alone." },
  { icon: UserCheck, title: "You decide", line: "Nothing is saved until you approve it." },
]

export function HowItWorksRoute() {
  return (
    <div className="relative isolate min-h-dvh overflow-hidden bg-background">
      <AmbientBackdrop />
      <TopBar />

      <main className="mx-auto max-w-5xl px-5 pb-20 sm:px-6">
        <header className="pb-12 pt-14 text-center sm:pt-20">
          <p className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/60 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground backdrop-blur">
            <Sparkles className="size-3 text-primary" aria-hidden /> How it works
          </p>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight sm:text-6xl">
            Four helpers.{" "}
            <span className="bg-gradient-to-r from-primary via-accent to-success bg-clip-text text-transparent">
              One goal.
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
            Get your real experience past the software and in front of a person.
          </p>
        </header>

        <ol className="grid gap-5 sm:grid-cols-2">
          {JOURNEYS.map((journey, index) => (
            <JourneyCard key={journey.title} journey={journey} number={index + 1} />
          ))}
        </ol>

        <section aria-label="Promises" className="mt-16">
          <div className="grid gap-4 rounded-3xl border bg-card/60 p-6 backdrop-blur sm:grid-cols-3 sm:p-8">
            {PROMISES.map(({ icon: Icon, title, line }) => (
              <div key={title} className="flex items-start gap-3 sm:flex-col sm:items-center sm:text-center">
                <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 text-primary">
                  <Icon className="size-6" aria-hidden />
                </span>
                <div>
                  <p className="font-semibold">{title}</p>
                  <p className="text-sm text-muted-foreground">{line}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-16 text-center">
          <Link to="/" className={cn(buttonVariants({ size: "lg" }))}>
            Start with your CV <ArrowRight aria-hidden />
          </Link>
          <p className="mt-4 text-xs text-muted-foreground">© 2026 PRAXA</p>
        </div>
      </main>
    </div>
  )
}

function JourneyCard({ journey, number }: { journey: Journey; number: number }) {
  const { icon: Icon, title, line, steps, tone } = journey
  return (
    <li
      className={cn(
        "group relative overflow-hidden rounded-3xl border bg-card/70 p-6 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:shadow-xl",
        tone.ring,
      )}
    >
      {/* A soft wash of the card's colour in one corner. */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute -right-16 -top-16 size-48 rounded-full bg-gradient-to-br to-transparent blur-2xl transition-opacity duration-300 group-hover:opacity-100 sm:opacity-70",
          tone.glow,
        )}
      />
      <div className="relative flex items-center justify-between">
        <span className={cn("grid size-12 place-items-center rounded-2xl", tone.badge)}>
          <Icon className="size-6" aria-hidden />
        </span>
        <span className="font-display text-4xl font-extrabold text-muted-foreground/20">
          0{number}
        </span>
      </div>
      <h2 className="relative mt-5 text-xl font-bold">{title}</h2>
      <p className="relative mt-1.5 text-muted-foreground">{line}</p>
      <ol className="relative mt-5 flex items-center gap-2" aria-label={`${title} in three steps`}>
        {steps.map((step, index) => (
          <li key={step} className="flex flex-1 items-center gap-2">
            <span className={cn("w-full rounded-full border px-3 py-1.5 text-center text-xs font-medium", tone.step)}>
              {step}
            </span>
            {index < steps.length - 1 ? (
              <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            ) : null}
          </li>
        ))}
      </ol>
    </li>
  )
}

function TopBar() {
  const { theme, toggle } = useTheme()
  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/70 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-5 sm:px-6">
        <Link to="/" className="font-display text-lg font-bold tracking-tight">
          PRAXA
        </Link>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          >
            {theme === "dark" ? <Sun aria-hidden /> : <Moon aria-hidden />}
          </Button>
          <Link to="/" className={cn(buttonVariants({ size: "sm", variant: "outline" }))}>
            <ArrowLeft aria-hidden /> Back
          </Link>
        </div>
      </div>
    </header>
  )
}
